import {FormControl, FormField, Separator} from 'platform/components';
import {Grid, GridItem, Heading, Hide, Show, VStack} from 'platform/foundation';

import {UseFormReturn, useWatch} from 'react-hook-form';

import {CustomerResponseBodyV2} from '@dms/api/customer';
import i18n from '@dms/i18n';
import {getOptionsFromCustomersAsSupplier} from '@dms/shared';

import {Nullish, TestIdProps, suffixTestId} from 'shared/web';

import {WarehouseDetailForm} from '../types/WarehouseDetailForm';
import {SupplierFieldList} from './SupplierFieldList';

interface SuppliersFormProps extends TestIdProps {
  control: FormControl<WarehouseDetailForm>;
  formApi: UseFormReturn<WarehouseDetailForm>;
  suppliers: CustomerResponseBodyV2[] | Nullish;
}

export function SuppliersForm(props: SuppliersFormProps) {
  const areAllSuppliersEnabled = useWatch({control: props.formApi.control, name: 'allSuppliers'});

  return (
    <VStack spacing={6}>
      <Heading size={3}>{i18n.t('entity.warehouse.labels.suppliers')}</Heading>
      <Grid columns={4}>
        <GridItem span={1}>
          <FormField
            name="allSuppliers"
            type="checkbox"
            label={i18n.t('entity.warehouse.labels.enableForAllSuppliers')}
            control={props.control}
            data-testid={suffixTestId('allSuppliers', props)}
          />
        </GridItem>
      </Grid>

      <Show when={areAllSuppliersEnabled}>
        <Grid columns={2}>
          <Separator spacing={0} />
        </Grid>
        <Grid columns={4}>
          <FormField
            isRequired
            isNotClearable
            name="defaultSupplierId"
            type="choice"
            label={i18n.t('entity.warehouse.labels.defaultSupplier')}
            options={getOptionsFromCustomersAsSupplier(props.suppliers)}
            onChange={(value) => props.formApi.setValue('suppliers', [{id: value as string}])}
            control={props.control}
            data-testid={suffixTestId('defaultSupplierId', props)}
          />
        </Grid>
      </Show>

      <Hide when={areAllSuppliersEnabled}>
        <Grid columns={2}>
          <Separator spacing={0} />
        </Grid>
        <SupplierFieldList<WarehouseDetailForm>
          isRequired
          name="suppliers"
          control={props.control}
          formApi={props.formApi}
          suppliers={props.suppliers}
          data-testid={suffixTestId('supplierFieldList', props)}
        />
      </Hide>
    </VStack>
  );
}
